import {
  DiscountType,
  DiscountScope,
  getAddOnRates,
  getDollarValue,
  getCentsValue,
  getField,
  Locales,
} from "@simplyk/common";

import { DiscountCodeData } from "./discountCode.types";

import { DiscountOutput, RateOutput } from "@/common/types/trpc";

// Type for backend discount with optional rate mappings
type DiscountWithRateMappings = DiscountOutput & {
  selectedRateIds?: string[];
  scope?: DiscountScope;
};

const extractRateIds = (selectedRates: DiscountCodeData["selectedRates"]): string[] => {
  if (!selectedRates) {
    return [];
  }
  return selectedRates.map((item) => (typeof item === "string" ? item : item.value));
};

export const convertDiscountAmount = (amount: number | undefined, unit: DiscountType): number => {
  if (!amount) {
    return 0;
  }
  const numericAmount = Number(amount);
  return unit === DiscountType.Dollars ? getCentsValue(numericAmount) : numericAmount;
};

const convertDiscountAmountFromBackend = (amount: number, type: DiscountType): number => {
  return type === DiscountType.Dollars ? getDollarValue(amount) : amount;
};

export const buildSelectedRates = (
  selectedRateIds: string[],
  rates: RateOutput[] | null | undefined,
  locale?: Locales
): Array<{ label: string; value: string }> => {
  if (!rates || selectedRateIds.length === 0) {
    return [];
  }

  return selectedRateIds.map((rateId) => {
    const rate = rates.find((r) => r.id === rateId);
    if (!rate) {
      return { label: rateId, value: rateId };
    }

    const rateField = locale ? getField(rate.rateFields, locale) : rate.rateFields?.[0];
    return {
      label: rateField?.title || "Rate",
      value: rateId,
    };
  });
};

export const mapDataToCreateDiscountCodes = (discounts: DiscountCodeData[] | undefined) => {
  if (!discounts) {
    return [];
  }

  return discounts.map((discount, index) => {
    const scope = discount.scope ?? DiscountScope.PerTicketAllRates;
    const selectedRateIds =
      scope === DiscountScope.PerTicketSpecificRates ? extractRateIds(discount.selectedRates) : undefined;

    return {
      id: discount.id,
      body: discount.body,
      amount: convertDiscountAmount(discount.amount, discount.unit),
      limit: discount.limit ?? null,
      type: discount.unit,
      sortIndex: index,
      selectedRateIds,
      scope,
    };
  });
};

export const mapDataToProvideDiscountCodes = (
  discounts: DiscountOutput[] | null | undefined,
  rates?: RateOutput[] | null,
  locale?: Locales
): DiscountCodeData[] => {
  if (!discounts) {
    return [];
  }

  const addOnRateIds = new Set(getAddOnRates(rates).map((rate) => rate.id));

  return discounts
    .slice()
    .sort((a, b) => a.sortIndex - b.sortIndex)
    .map((discount) => {
      const discountWithMappings = discount as DiscountWithRateMappings;
      const scope = discountWithMappings.scope ?? DiscountScope.PerTicketAllRates;
      const selectedRateIds = (discountWithMappings.selectedRateIds ?? []).filter(
        (rateId) => !addOnRateIds.has(rateId)
      );
      const selectedRates = buildSelectedRates(selectedRateIds, rates, locale);

      return {
        id: discount.id,
        body: discount.body,
        amount: convertDiscountAmountFromBackend(discount.amount, discount.type),
        unit: discount.type,
        limit: discount.limit ?? null,
        selectedRates,
        scope,
      };
    });
};
